import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Mail, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e2) {
      // Backend returns 200 in all cases; only surface real network errors.
      setErr(e2.response?.data?.detail || e2.message || "Could not send reset link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16 grain relative" data-testid="forgot-password-page">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(201,169,97,0.08),_transparent_60%)]" />
      <div className="relative card-bento w-full max-w-md">
        <div className="overline mb-3">Reset Password</div>
        <h1
          className="text-3xl md:text-4xl text-[#F7F5F0] mb-2"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          {sent ? "Check your email." : "Forgot your password?"}
        </h1>
        <p className="text-sm text-[#8A8782] mb-8">
          {sent
            ? "If an account exists for that email, a reset link is on its way. Links expire in 30 minutes."
            : "Tell us the email on your account. If it's only used by one person, we'll send a reset link."}
        </p>

        {!sent && (
          <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
            <div>
              <label className="block text-sm text-[#D1CFC9] mb-2 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-[#C9A961]" /> Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-tcpi"
                placeholder="you@example.com"
                data-testid="forgot-email-input"
              />
            </div>

            {err && (
              <div className="text-sm text-red-400" data-testid="forgot-error">{err}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-60"
              data-testid="forgot-submit-btn"
            >
              {loading ? "Sending…" : "Send reset link"}
            </button>
          </form>
        )}

        {sent && (
          <div className="space-y-3" data-testid="forgot-sent-message">
            <div className="p-4 rounded-md border border-[#C9A961]/30 bg-[#1A1816] text-sm text-[#D1CFC9]">
              <p>If you don't see it in a few minutes, check spam, then try again.</p>
            </div>
            <div className="p-4 rounded-md border border-amber-500/40 bg-amber-500/5 text-sm text-[#D1CFC9]">
              <div className="overline text-[10px] text-amber-300 mb-1.5">Cohort account?</div>
              <p>
                If your email is shared with other accounts (a cohort, family, or shelter
                that signs in together), automatic resets are turned off for safety.
                <strong className="text-[#F7F5F0]"> Your facilitator can reset your
                password from the admin panel.</strong>
              </p>
            </div>
          </div>
        )}

        <div className="mt-6 text-sm text-center">
          <Link to="/login" className="text-[#C9A961] hover:underline inline-flex items-center gap-1.5" data-testid="forgot-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
